import React, { useState } from 'react';
import { 
  Settings as SettingsIcon, 
  Building2,
  Users,
  Wifi,
  Database,
  Shield,
  Bell,
  Palette,
  Globe,
  Save,
  AlertTriangle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import BrandingSettings from './BrandingSettings';

const Settings: React.FC = () => {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [activeTab, setActiveTab] = useState('organization');

  const tabs = [
    { id: 'organization', label: 'Organization', icon: Building2 },
    { id: 'branding', label: 'Branding', icon: Palette },
    { id: 'users', label: 'Users & Roles', icon: Users },
    { id: 'integrations', label: 'Integrations', icon: Wifi },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'system', label: 'System', icon: Database },
  ];

  const OrganizationTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-heading-3 text-[var(--text-primary)] mb-4">Organization Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
              Organization Name
            </label>
            <input
              type="text"
              defaultValue="Occitanie Cooperative"
              className="input-field"
              data-testid="org-name-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
              Organization Type
            </label>
            <select className="input-field" defaultValue="coop">
              <option value="coop">Cooperative</option>
              <option value="farm">Farm</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
              Contact Email
            </label>
            <input
              type="email"
              defaultValue="contact@occitanie-coop.fr"
              className="input-field"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              defaultValue="+33 4 67 12 34 56"
              className="input-field"
            />
          </div>
        </div>
        
        <div className="mt-6">
          <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
            Address
          </label>
          <textarea
            className="input-field"
            rows={3}
            defaultValue="123 Avenue des Vignes, 34000 Montpellier, France"
          />
        </div>
        
        <div className="mt-6">
          <button className="btn-primary">Save Organization Settings</button>
        </div>
      </div>
    </div>
  );

  const UsersTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-heading-3 text-[var(--text-primary)]">Users & Roles</h3>
        <button className="btn-primary">Invite User</button>
      </div>
      
      <div className="bg-[var(--surface)] rounded-lg overflow-hidden border border-[var(--border-light)]">
        <table className="w-full">
          <thead className="bg-[var(--gray-50)]">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-[var(--text-primary)]">Name</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-[var(--text-primary)]">Email</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-[var(--text-primary)]">Role</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-[var(--text-primary)]">Status</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-[var(--text-primary)]">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--border-light)]">
            <tr>
              <td className="px-4 py-3 text-sm text-[var(--text-primary)]">Jean Dupont</td>
              <td className="px-4 py-3 text-sm text-[var(--text-secondary)]">jean@occitanie-coop.fr</td>
              <td className="px-4 py-3">
                <span className="px-2 py-1 text-xs bg-[var(--color-primary)] text-white rounded-full">
                  Coop Admin
                </span>
              </td>
              <td className="px-4 py-3">
                <span className="px-2 py-1 text-xs bg-[var(--color-success)] text-white rounded-full">
                  Active
                </span>
              </td>
              <td className="px-4 py-3">
                <button className="text-sm text-[var(--color-primary)] hover:text-[var(--color-primary-700)]">
                  Edit
                </button>
              </td>
            </tr>
            <tr>
              <td className="px-4 py-3 text-sm text-[var(--text-primary)]">Marie Laurent</td>
              <td className="px-4 py-3 text-sm text-[var(--text-secondary)]">marie@occitanie-coop.fr</td>
              <td className="px-4 py-3">
                <span className="px-2 py-1 text-xs bg-[var(--color-info)] text-white rounded-full">
                  Farmer
                </span>
              </td>
              <td className="px-4 py-3">
                <span className="px-2 py-1 text-xs bg-[var(--color-success)] text-white rounded-full">
                  Active
                </span>
              </td>
              <td className="px-4 py-3">
                <button className="text-sm text-[var(--color-primary)] hover:text-[var(--color-primary-700)]">
                  Edit
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );

  const IntegrationsTab = () => (
    <div className="space-y-6">
      <h3 className="text-heading-3 text-[var(--text-primary)]">System Integrations</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-[var(--color-info)] rounded-lg flex items-center justify-center">
              <Wifi className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h4 className="text-heading-4 text-[var(--text-primary)]">AWS IoT Core</h4>
              <p className="text-body-sm text-[var(--text-secondary)]">Device connectivity and data ingestion</p>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-[var(--color-success)] rounded-full"></div>
              <span className="text-sm text-[var(--color-success)]">Connected</span>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-[var(--border-light)]">
            <button className="text-sm text-[var(--color-primary)] hover:text-[var(--color-primary-700)]">
              Configure
            </button>
          </div>
        </div>

        <div className="card opacity-60">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-[var(--color-warning)] rounded-lg flex items-center justify-center">
              <Database className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h4 className="text-heading-4 text-[var(--text-primary)]">Valley365</h4>
              <p className="text-body-sm text-[var(--text-secondary)]">Irrigation management platform</p>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-[var(--color-warning)] rounded-full"></div>
              <span className="text-sm text-[var(--color-warning)]">Pending</span>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-[var(--border-light)]">
            <button className="text-sm text-[var(--color-primary)] hover:text-[var(--color-primary-700)]">
              Setup Integration
            </button>
          </div>
        </div>

        <div className="card opacity-60">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-[var(--color-secondary)] rounded-lg flex items-center justify-center">
              <Globe className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h4 className="text-heading-4 text-[var(--text-primary)]">Satellite Data</h4>
              <p className="text-body-sm text-[var(--text-secondary)]">Weather and field condition data</p>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-[var(--text-secondary)] rounded-full"></div>
              <span className="text-sm text-[var(--text-secondary)]">Not configured</span>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-[var(--border-light)]">
            <button className="text-sm text-[var(--color-primary)] hover:text-[var(--color-primary-700)]">
              Setup Integration
            </button>
          </div>
        </div>
      </div>
      
      <div className="card bg-gradient-accent bg-opacity-10 border border-[var(--color-warning)]">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="w-5 h-5 text-[var(--color-warning)] mt-1" />
          <div>
            <h4 className="text-heading-4 text-[var(--color-warning)] mb-2">Integration Notice</h4>
            <p className="text-body-sm text-[var(--text-secondary)]">
              Some integrations are placeholders in this MVP demo. Real implementations would require proper API credentials and configuration.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const NotificationsTab = () => (
    <div className="space-y-6">
      <h3 className="text-heading-3 text-[var(--text-primary)]">Notification Preferences</h3>
      
      <div className="space-y-4">
        <div className="flex items-center justify-between p-4 bg-[var(--surface)] border border-[var(--border-light)] rounded-lg">
          <div>
            <h4 className="text-heading-4 text-[var(--text-primary)]">Critical Alerts</h4>
            <p className="text-body-sm text-[var(--text-secondary)]">
              Receive notifications for critical system alerts (leaks, equipment failures)
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" defaultChecked className="sr-only peer" />
            <div className="w-11 h-6 bg-[var(--gray-200)] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[var(--color-primary)]"></div>
          </label>
        </div>
        
        <div className="flex items-center justify-between p-4 bg-[var(--surface)] border border-[var(--border-light)] rounded-lg">
          <div>
            <h4 className="text-heading-4 text-[var(--text-primary)]">Maintenance Reminders</h4>
            <p className="text-body-sm text-[var(--text-secondary)]">
              Get notified about scheduled maintenance and work orders
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" defaultChecked className="sr-only peer" />
            <div className="w-11 h-6 bg-[var(--gray-200)] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[var(--color-primary)]"></div>
          </label>
        </div>
        
        <div className="flex items-center justify-between p-4 bg-[var(--surface)] border border-[var(--border-light)] rounded-lg">
          <div>
            <h4 className="text-heading-4 text-[var(--text-primary)]">Daily Reports</h4>
            <p className="text-body-sm text-[var(--text-secondary)]">
              Receive daily summary reports via email
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" className="sr-only peer" />
            <div className="w-11 h-6 bg-[var(--gray-200)] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[var(--color-primary)]"></div>
          </label>
        </div>
      </div>
    </div>
  );

  const SystemTab = () => (
    <div className="space-y-6">
      <h3 className="text-heading-3 text-[var(--text-primary)]">System Preferences</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card">
          <div className="flex items-center space-x-3 mb-4">
            <Palette className="w-5 h-5 text-[var(--color-primary)]" />
            <h4 className="text-heading-4 text-[var(--text-primary)]">Appearance</h4>
          </div>
          
          <div className="space-y-3">
            <button
              onClick={toggleTheme}
              className={`w-full p-3 text-left border rounded-lg transition-colors ${
                theme === 'light' 
                  ? 'border-[var(--color-primary)] bg-[var(--color-primary-50)]' 
                  : 'border-[var(--border-light)] hover:bg-[var(--gray-50)]'
              }`}
            >
              <div className="font-medium">Light Mode</div>
              <div className="text-sm text-[var(--text-secondary)]">Clean, bright interface</div>
            </button>
            
            <button
              onClick={toggleTheme}
              className={`w-full p-3 text-left border rounded-lg transition-colors ${
                theme === 'dark' 
                  ? 'border-[var(--color-primary)] bg-[var(--color-primary-50)]' 
                  : 'border-[var(--border-light)] hover:bg-[var(--gray-50)]'
              }`}
            >
              <div className="font-medium">Dark Mode</div>
              <div className="text-sm text-[var(--text-secondary)]">Easy on the eyes</div>
            </button>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center space-x-3 mb-4">
            <Globe className="w-5 h-5 text-[var(--color-primary)]" />
            <h4 className="text-heading-4 text-[var(--text-primary)]">Localization</h4>
          </div>
          
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                Language
              </label>
              <select className="input-field" defaultValue="fr">
                <option value="fr">Français</option>
                <option value="en">English</option>
                <option value="es">Español</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                Timezone
              </label>
              <select className="input-field" defaultValue="Europe/Paris">
                <option value="Europe/Paris">Europe/Paris (CET)</option>
                <option value="UTC">UTC</option>
                <option value="Europe/London">Europe/London (GMT)</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      
      <div className="flex items-center space-x-4">
        <button className="btn-primary flex items-center space-x-2">
          <Save className="w-4 h-4" />
          <span>Save Preferences</span>
        </button>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'organization': return <OrganizationTab />;
      case 'branding': return <BrandingSettings />;
      case 'users': return <UsersTab />;
      case 'integrations': return <IntegrationsTab />;
      case 'notifications': return <NotificationsTab />;
      case 'system': return <SystemTab />;
      default: return <OrganizationTab />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-heading-2 text-[var(--text-primary)]">Settings</h1>
        <p className="text-body text-[var(--text-secondary)] mt-1">
          Manage organization, users, and system preferences
        </p>
      </div>

      {/* Settings Navigation */}
      <div className="border-b border-[var(--border-light)]">
        <nav className="flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-[var(--color-primary)] text-[var(--color-primary)]'
                    : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--border-medium)]'
                }`}
                data-testid={`settings-tab-${tab.id}`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="py-6">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default Settings;